import { create } from 'zustand';

/**
 * Which visual the global loader shows:
 *  - 'default'    → the standard branded spinner overlay (AppLoaderOverlay).
 *  - 'simulation' → the cinematic, personalized interview-generation
 *                   experience (InterviewGeneratingOverlay).
 */
export type LoadingVariant = 'default' | 'simulation';

type UiState = {
  pendingRequests: number;
  isGlobalLoading: boolean;
  /** Optional context line shown under the global loader's gator mark. */
  loadingMessage: string | null;
  /** Selects which loader visual to render while loading. */
  loadingVariant: LoadingVariant;
  startRequest: (message?: string, variant?: LoadingVariant) => void;
  finishRequest: () => void;
  resetLoading: () => void;
};

export const useUiStore = create<UiState>((set) => ({
  pendingRequests: 0,
  isGlobalLoading: false,
  loadingMessage: null,
  loadingVariant: 'default',
  startRequest: (message, variant) =>
    set((state) => {
      const nextPending = state.pendingRequests + 1;
      return {
        pendingRequests: nextPending,
        isGlobalLoading: nextPending > 0,
        // A caller with a specific message wins over the generic default;
        // never downgrade an existing message to null mid-flight.
        loadingMessage: message ?? state.loadingMessage,
        // Same rule for the variant: a specific variant set by the initiating
        // caller is preserved across the nested API requests it kicks off
        // (each of which calls startRequest without a variant).
        loadingVariant: variant ?? state.loadingVariant,
      };
    }),
  finishRequest: () =>
    set((state) => {
      const nextPending = Math.max(0, state.pendingRequests - 1);
      return {
        pendingRequests: nextPending,
        isGlobalLoading: nextPending > 0,
        loadingMessage: nextPending > 0 ? state.loadingMessage : null,
        loadingVariant: nextPending > 0 ? state.loadingVariant : 'default',
      };
    }),
  resetLoading: () =>
    set({ pendingRequests: 0, isGlobalLoading: false, loadingMessage: null, loadingVariant: 'default' }),
}));

export function startGlobalLoading(message?: string, variant?: LoadingVariant) {
  useUiStore.getState().startRequest(message, variant);
}

export function finishGlobalLoading() {
  useUiStore.getState().finishRequest();
}
